// Mirrors buildStatusUpdate in ghostfit-console/lib/store-change-requests.js,
// which refuses done/declined without a note. Asking here only saves a round
// trip — the server rule is the one that holds.
export function resolutionPrompt(status: string): string | null {
  if (status === 'done') return 'What did you change?';
  if (status === 'declined') return 'Why is this being declined?';
  return null;
}

export function needsResolution(status: string): boolean {
  return resolutionPrompt(status) !== null;
}

// teamstore-proof hardcodes requestedBy:'rep' but always stamps
// requestFingerprint, so the fingerprint is what identifies the customer.
export function requestSource(r: { requestFingerprint?: string; requestedBy?: string }): string {
  return typeof r.requestFingerprint === 'string' && r.requestFingerprint
    ? 'Customer (proof page)'
    : (r.requestedBy || '');
}

export type StatusChange = 'in_progress' | 'done' | 'declined';

// What to send for a status change, given the answer to the prompt.
// A null answer means the prompt was cancelled (or suppressed by the browser).
export function reasonToSend(
  status: string,
  answer: string | null,
): { send: true; reason: string | null } | { send: false; message: string | null } {
  if (!needsResolution(status)) return { send: true, reason: null };
  if (answer === null) return { send: false, message: null };
  const reason = answer.trim();
  if (!reason) {
    return {
      send: false,
      message: status === 'done' ? 'Done needs a note saying what you changed.' : 'Declining needs a reason.',
    };
  }
  return { send: true, reason };
}

// How to report a failed status change. A request closed or deleted meanwhile
// means this list is stale: say so and refresh it.
export function statusChangeFailure(httpStatus: number | null, body: unknown): { message: string; refresh: boolean } {
  const b = (body && typeof body === 'object' ? body : {}) as { error?: unknown; errors?: unknown };
  if (httpStatus === 409 && b.error === 'request_closed') return { message: 'Already closed — list refreshed.', refresh: true };
  if (httpStatus === 404) return { message: 'That request no longer exists — list refreshed.', refresh: true };
  if (httpStatus === 400 && Array.isArray(b.errors) && b.errors.length) {
    return { message: b.errors.map(String).join(' · '), refresh: false };
  }
  return { message: 'Could not update that request.', refresh: false };
}
